import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef, Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import {
    MODAL_EDITOR_TOKEN,
    ModalEditorBase
} from '../../../classes/modal-editor-base';
import { EditorLocation } from 'apps/account/src/account-shared';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
// @ts-ignore
import { APILoader } from '@googlemaps/extended-component-library/api_loader.js';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { formatAddress } from '../../../utils/address.util';

@Component({
    selector: 'ft-address-editor',
    templateUrl: './address-editor.component.html',
    styleUrls: ['./address-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => AddressEditorComponent)
        },
        {
            provide: MODAL_EDITOR_TOKEN,
            useExisting: AddressEditorComponent
        }
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AddressEditorComponent
    extends ModalEditorBase
    implements AfterViewInit, OnDestroy, OnInit {
    @Input() isStructuredLocation = false;
    @Input() isVenue: boolean;
    @Input() isManualEntry = true;
    @ViewChild('lookupRef', { static: false }) lookupRef: ElementRef;
    @ViewChild('loaderRef', { static: true }) loeaderRef: ElementRef;
    @ViewChildren('inputs', { read: NgModel }) inputs: QueryList<NgModel>;
    @Output() editorVisible = new EventEmitter();

    fullAddress: string;
    overlaySelector = 'cdk-global-overlay-wrapper';
    initialValue: EditorLocation = {
        streetAddress: null,
        countryName: null,
        region: null,
        city: null,
        postalCode: null,
        latitude: null,
        longitude: null,
        area: null,
        googlePlaceId: null
    };
    baseFields: EditorLocation = {
        ...this.initialValue
    };

    googlePlacesModule: any;
    autocompleteInstance: any;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        @Inject(ENVIRONMENT_TOKEN) private environment: Environment
    ) {
        super();
        this.value$
            .pipe(
                tap((value) => {
                    if (!value) {
                        this.initValue();
                    }
                }),
                filter((value) => !!value),
                map((value) => ({ ...this.baseFields, ...value })),
                tap((value) => {
                    this.fullAddress = this.formatAddress(value);
                    this.updateLanguageData(value);
                }),
                takeUntil(this.destroySubj)
            )
            .subscribe();

        this.onModelInit()
            .pipe(
                tap((value) => {
                    this.initValue(value);
                }),
                takeUntil(this.destroySubj)
            )
            .subscribe();
    }

    updateLanguageData(location: EditorLocation) {
        location.languageData?.forEach(data => {
            data.city = location.city;
            data.region = location.region;
            data.postalCode = location.postalCode;
            data.area = location.area;
            data.name = location.name;
            data.streetAddress = location.streetAddress;
            data.googlePlaceId = location?.googlePlaceId;
            data.fullAddress = location?.fullAddress;
        });
    }

    formatAddress = formatAddress;

    initValue(value?: any) {
        this.value = {
            ...this.baseFields,
            ...value,
            latitude: isNaN(value?.latitude) ? null : value?.latitude,
            longitude: isNaN(value?.longitude) ? null : value?.longitude
        };

        if (this.isVenue) {
            this.value = {
                ...this.value,
                name: this.value?.name
            };
        }
    }

    onEditorVisible() {
        super.onEditorVisible();
        document
            .getElementsByClassName(this.overlaySelector)?.[0]
            ?.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        this.editorVisible.emit();
    }

    onEditorHidden() {
        super.onEditorHidden();
        // Clean up Google Places autocomplete to prevent menu from persisting
        if (this.autocompleteInstance) {
            // Clear all listeners
            (window as any)?.google?.maps?.event?.clearInstanceListeners?.(this.autocompleteInstance);
            // Blur the input field to ensure dropdown closes
            if (this.lookupRef?.nativeElement) {
                this.lookupRef.nativeElement.blur();
            }
            // Reset the autocomplete instance
            this.autocompleteInstance = null;
        }
    }

    ngOnInit() {
        this.loeaderRef.nativeElement.setAttribute('key', this.environment.googlePlacesApiKey);
        this.updateInitialValue();
    }

    initAutocomplete() {
        // Clean up any existing autocomplete instance
        if (this.autocompleteInstance) {
            (window as any)?.google?.maps?.event?.clearInstanceListeners?.(this.autocompleteInstance);
            this.autocompleteInstance = null;
        }

        const componentForm = {
            street_number: 'short_name',
            route: 'long_name',
            locality: 'long_name',
            administrative_area_level_1: 'short_name',
            country: 'short_name',
            postal_code: 'short_name'
        };
        // Create the autocomplete object, restricting the search to full addresses.
        this.autocompleteInstance = new this.googlePlacesModule.Autocomplete(
            this.lookupRef.nativeElement,
            { places: this.isVenue ? [] : ['address'] }
        );

        // When the user selects an address from the dropdown, populate the address fields in the form.
        this.autocompleteInstance.addListener('place_changed', () => {
            // Get the place details from the autocomplete object.
            const place = this.autocompleteInstance.getPlace();

            // Get each component of the address from the place details and populate an easy to read object
            const location: any = {
                lat: place.geometry.location.lat(),
                long: place.geometry.location.lng()
            };
            for (let i = 0; i < place.address_components.length; i++) {
                // looks like google API changed and we can get multiple results per address component level, i.e. "political, administrative_area_level_1"
                const addressType = place.address_components[i].types?.find(type => Object.keys(componentForm).some(key => key === type));
                if (componentForm[addressType]) {
                    location[addressType] =
                        place.address_components[i][componentForm[addressType]];
                }
            }

            // Convert our object into a location object
            let cleanLocation: Partial<EditorLocation> = {
                streetAddress:
                    (location.street_number
                        ? `${location.street_number} `
                        : '') + (location.route || '') || '',
                countryName: location.country || '',
                region: location.administrative_area_level_1 || '',
                city:
                    location.locality ||
                    place.address_components.find((item) =>
                        item.types.some(
                            (type) => type === 'administrative_area_level_1'
                        )
                    )?.long_name ||
                    '',
                postalCode: location.postal_code || '',
                latitude: location.lat || '',
                longitude: location.long || '',
                googlePlaceId: place.place_id || ''
            };

            if (this.isVenue) {
                cleanLocation = { ...cleanLocation, name: place.name || '' };
            }

            const newValue = { ...this.value, ...cleanLocation };
            newValue.fullAddress = this.formatAddress(newValue);
            this.updateLanguageData(newValue);
            this.value = { ...newValue };
            this.changeDetectorRef.detectChanges();
        });
    }

    async ngAfterViewInit() {
        this.googlePlacesModule = await APILoader.importLibrary('places');

        this.initAutocomplete();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        document
            .getElementsByClassName(this.overlaySelector)?.[0]
            ?.removeEventListener('scroll', this.onScroll.bind(this));
        // Clean up Google Places autocomplete
        if (this.autocompleteInstance) {
            (window as any)?.google?.maps?.event?.clearInstanceListeners?.(this.autocompleteInstance);
            this.autocompleteInstance = null;
        }
    }

    onScroll() {
        if (document.activeElement === this.lookupRef.nativeElement) {
            this.lookupRef.nativeElement.blur();
        }
    }

    isInvalid(): boolean {
        return this.inputs?.some((input) => input.invalid);
    }
}
